import { useCallback, useRef } from 'react';

import { Key } from 'common/enums/Keyboard';
import type { DispatchState } from 'common/types/React';
import type { CurrentNavigation } from '../types';
import { navigateDown, navigateLeft, navigateRight, navigateUp } from './keyboard-nav-utils';

interface UseKeyboardNavigationOptions {
  isOpen: boolean;
  isCascading: boolean;
  currentNavigation: CurrentNavigation;
  setCurrentNavigation: DispatchState<CurrentNavigation>;
}

interface GetNavigationHandlerOptions extends Pick<
  UseKeyboardNavigationOptions,
  'currentNavigation' | 'setCurrentNavigation'
> {
  container: Element;
}

const getNavigationHandler =
  ({ container, currentNavigation, setCurrentNavigation }: GetNavigationHandlerOptions) =>
  (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (event.code === Key.ArrowDown) {
      navigateDown(target, container);
    }
    if (event.code === Key.ArrowUp) {
      navigateUp(target, container, currentNavigation);
    }
    if (
      [Key.ArrowRight, Key.Space, Key.Enter].includes(event.code as Key) &&
      target instanceof HTMLButtonElement &&
      target.closest('li')
    ) {
      navigateRight(event, container);
    }
    if (event.code === Key.ArrowLeft && currentNavigation.length > 0) {
      navigateLeft(currentNavigation, setCurrentNavigation, container);
    }
  };

export const useSideNavKeyboardNavigation = ({
  isOpen,
  isCascading,
  currentNavigation,
  setCurrentNavigation,
}: UseKeyboardNavigationOptions) => {
  const drawerRef = useRef(null);
  const getHandler = useCallback(() => {
    if (!isCascading || !isOpen || !drawerRef.current) {
      return;
    }

    return getNavigationHandler({
      container: drawerRef.current,
      currentNavigation,
      setCurrentNavigation,
    });
  }, [isOpen, isCascading, drawerRef, currentNavigation, setCurrentNavigation]);

  return { drawerRef, keyDownHandler: getHandler() };
};
